import React from 'react';
import {useSelector} from 'react-redux';
import {useActiveYear} from 'src/common/hooks/useActiveYear';
import {TaxProfileSubsection} from 'src/constants/constants';
import {TaxProfileField} from 'src/store/taxProfile/taxProfile.types';
import {TaxProfileQuestion} from 'src/store/taxProfile/taxProfile.types';
import SimpleKeyValueField from 'src/CpaCenterV2/TaxProfileSections/common/SimpleKeyValueField';
import SubSectionBox from 'src/CpaCenterV2/TaxProfileSections/common/SubSectionBox';
import {FILING_STATUS_ANSWER} from 'src/store/taxProfile/taxProfile.types';
import {ReduxStateType} from 'src/store/store';
import {
  selectTaxProfile,
  selectTaxProfileAnswer,
} from 'src/store/taxProfile/taxProfile.selector';

interface SpouseDetailsProps {
  style?: React.CSSProperties;
  onEdit?: (subsection: TaxProfileSubsection) => void;
}

const SpouseDetails = ({style, onEdit}: SpouseDetailsProps) => {
  const {loaded} = useSelector(selectTaxProfile);
  const {activeYear} = useActiveYear();

  const filingStatus = useSelector((state: ReduxStateType) =>
    selectTaxProfileAnswer(
      state,
      TaxProfileQuestion.FILING_STATUS,
      activeYear,
    ),
  );
  const spouseLegalName = useSelector((state: ReduxStateType) =>
    selectTaxProfileAnswer(
      state,
      TaxProfileQuestion.SPOUSE_LEGAL_NAME,
      activeYear,
    ),
  );
  const spouseSSN = useSelector((state: ReduxStateType) =>
    selectTaxProfileAnswer(state, TaxProfileQuestion.SPOUSE_SSN, activeYear),
  );
  const spouseDOB = useSelector((state: ReduxStateType) =>
    selectTaxProfileAnswer(state, TaxProfileQuestion.SPOUSE_DOB, activeYear),
  );
  const spouseEmail = useSelector((state: ReduxStateType) =>
    selectTaxProfileAnswer(state, TaxProfileQuestion.SPOUSE_EMAIL, activeYear),
  );

  const canSomeoneElseClaimSpouseAsDependent = useSelector(
    (state: ReduxStateType) =>
      selectTaxProfileAnswer(
        state,
        TaxProfileQuestion.SPOUSE_CLAIMED_DEPENDENT,
        activeYear,
      ),
  );

  return (
    <SubSectionBox
      name="Spouse details"
      style={style}
      onEdit={onEdit}
      taxprofileSubsection={TaxProfileSubsection.SpouseDetails}>
      {[
        FILING_STATUS_ANSWER.MARRIED_FILING_JOINTLY,
        FILING_STATUS_ANSWER.MARRIED_FILING_SEPARAELY,
      ].includes(filingStatus) && (
        <>
          <SimpleKeyValueField
            name="Spouse first name & middle initial"
            style={{marginBottom: 12}}
            value={spouseLegalName?.[TaxProfileField.FIRST_NAME]}
            loading={!loaded}
          />
          <SimpleKeyValueField
            name="Spouse last name"
            style={{marginBottom: 12}}
            value={spouseLegalName?.[TaxProfileField.LAST_NAME]}
            loading={!loaded}
          />
          <SimpleKeyValueField
            name="Social Security Number"
            style={{marginBottom: 12}}
            value={spouseSSN}
            loading={!loaded}
          />
          {filingStatus === FILING_STATUS_ANSWER.MARRIED_FILING_JOINTLY && (
            <>
              <SimpleKeyValueField
                name="Date of birth"
                style={{marginBottom: 12}}
                value={spouseDOB}
                loading={!loaded}
              />
              <SimpleKeyValueField
                name="Spouse email"
                style={{marginBottom: 12}}
                value={spouseEmail}
                loading={!loaded}
              />
              <SimpleKeyValueField
                name="Can your spouse be claimed as someone’s dependent?"
                value={canSomeoneElseClaimSpouseAsDependent}
                style={{marginBottom: 12}}
                loading={!loaded}
              />
            </>
          )}
        </>
      )}
    </SubSectionBox>
  );
};

export default SpouseDetails;
